import type { KeyboardEvent, ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { differenceInCalendarDays, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import type { MessageDescriptor } from '@lingui/core';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';
import type { WorkflowItemResponse } from '@/types/workflow';
import {
  getUrgency,
  getCategoryLabel,
  getActionLabel,
  getCategoryColorClass,
  payloadSummary,
  isCareAlertCategory,
} from '@/types/workflow';
import { useDunningWorkflowDrawer } from '@/contexts/DunningWorkflowDrawerContext';
import { useWorkflowDetailGate } from '@/hooks/useWorkflowDetailGate';
import { ModuleUnavailableHint } from '@/components/auth/ModuleUnavailableHint';
import {
  parseChangeRequestPayload,
  getChangeRequestTypeLabel,
  isSensitiveSubType,
  formatConfidence,
  getSourceLabel,
} from '@/types/changeRequest';
import { formatDateDisplay, formatDateTime, getDateFnsLocale } from '@/lib/format';
import { formatMoneyCents } from '@/lib/money';
import { buildChangeRequestSubjectText } from './changeRequestSubjectLine';
import { AccidentSeverityBadge } from './AccidentSeverityBadge';
import { BatchLockHint } from './BatchLockHint';
import { coverageBreachSummary } from '@/types/coverageBreach';
import { subsidyReportSummary } from '@/types/subsidyReport';
import { subsidyExpirySummary } from '@/types/subsidyExpiry';
import { lateExtrasSummary } from '@/types/lateExtras';
import { CARE_ALERT_SUMMARY } from '@/types/careAlertSummary';
import { escalationTargetLabel, type Responsible } from '@/types/workflowResponsible';
import { ResponsibleCell } from './ResponsibleCell';

interface WorkflowBoardCardProps {
  item: WorkflowItemResponse & { id: string };
  onOpenDetail: (itemId: string) => void;
  onActionClick: (itemId: string, action: string, currentState: string) => void;
  /** Why "Approve all" leaves this change request out; shows a lock with the reason. */
  lockReason?: ReactNode;
  /**
   * Who has to act, shown under the summary. Left out on the board grouped by
   * person: there the column already says who.
   */
  responsible?: Responsible;
  /**
   * Tag an open card nobody holds with "Not taken". The board grouped by person
   * sets it: a lead's or a role's column answers for the item, but nobody took it.
   */
  markNotTaken?: boolean;
}

const DUNNING_STAGE_LABELS: Record<string, MessageDescriptor> = {
  current: msg({ id: 'dunning.kanban.stage.current', message: 'Current' }),
  reminder_1_sent: msg({ id: 'dunning.kanban.stage.reminder_1_sent', message: 'R1 sent' }),
  reminder_2_sent: msg({ id: 'dunning.kanban.stage.reminder_2_sent', message: 'R2 sent' }),
  reminder_3_sent: msg({ id: 'dunning.kanban.stage.reminder_3_sent', message: 'R3 sent' }),
  final_notice_sent: msg({ id: 'dunning.kanban.stage.final_notice_sent', message: 'Final' }),
  paid: msg({ id: 'dunning.kanban.stage.paid', message: 'Paid' }),
  collections_handover: msg({ id: 'dunning.kanban.stage.collections_handover', message: 'Handover' }),
  cancelled: msg({ id: 'dunning.kanban.stage.cancelled', message: 'Cancelled' }),
};

export function WorkflowBoardCard({
  item,
  onOpenDetail,
  onActionClick,
  lockReason,
  responsible,
  markNotTaken = false,
}: WorkflowBoardCardProps) {
  const { i18n } = useLingui();
  const dunningDrawer = useDunningWorkflowDrawer();
  const canOpenDetail = useWorkflowDetailGate();
  const handleOpenForItem = (id: string) => {
    if (item.category === 'dunning' && dunningDrawer) {
      dunningDrawer.openDrawerForWorkflow(id);
    } else {
      onOpenDetail(id);
    }
  };

  const payload = item.payload as Record<string, unknown> | undefined;
  const summary =
    item.category === 'subsidy_report'
      ? subsidyReportSummary(i18n, payload)
      : item.category === 'subsidy_expiry'
        ? subsidyExpirySummary(i18n, payload)
        : item.category === 'late_extras'
          ? lateExtrasSummary(i18n, payload)
          : item.category === 'coverage_breach'
            ? (coverageBreachSummary(i18n, payload, i18n.locale) ?? payloadSummary(payload))
            : isCareAlertCategory(item.category)
              ? CARE_ALERT_SUMMARY[item.category](i18n, payload)
              : payloadSummary(payload);
  // An open card nobody holds: the column may name a group lead or a role, but nobody took it.
  const notTakenTag =
    markNotTaken && !item.assigned_to && !item.completed_at ? (
      <Badge variant="outline" className="text-xs px-1.5 py-0 w-fit" data-testid={`board-card-not-taken-${item.id}`}>
        {i18n._({ id: 'workflow.board.notTaken', message: 'Not taken' })}
      </Badge>
    ) : null;
  const severity = payload?.severity as string | undefined;
  const urgency = getUrgency(item.due_at);
  const escalationLabel = escalationTargetLabel(i18n, item);
  // late_extras (#2028): the card opens the detail, where both decisions sit
  // next to the list; a bare "Skip" on the card would be the wrong first action.
  const actions = item.category === 'late_extras' ? [] : (item.available_actions ?? []);
  const categoryColorClass = getCategoryColorClass(item.category);

  // Dunning category renders a category-specific layout: household name (from
  // reference_label, if exposed), stage badge, days-overdue chip, outstanding
  // amount.
  if (item.category === 'dunning') {
    const outstandingCents = (payload?.outstanding_cents_snapshot as number) ?? 0;
    const currency = (payload?.currency as string) ?? 'CHF';
    const lastSent = payload?.last_sent_at as string | undefined;
    const stageDescriptor = DUNNING_STAGE_LABELS[item.current_state ?? ''];
    const stageLabel = stageDescriptor ? i18n._(stageDescriptor) : (item.current_state ?? '');
    const householdName =
      ((item as unknown as { reference_label?: string }).reference_label) ||
      ((payload?.household_name as string | undefined)) ||
      '—';
    const isPaused = !!(payload?.paused_indefinite || payload?.paused_until);
    const oldestOverdue = (item as unknown as { oldest_overdue_date?: string }).oldest_overdue_date;
    const daysOverdue = oldestOverdue ? Math.max(0, differenceInCalendarDays(new Date(), parseISO(oldestOverdue))) : null;
    // The drawer reads `GET /dunning/workflows/:id` (`billing`); without it the
    // card stays but does not open (#1691).
    const canOpen = canOpenDetail(item.category);
    const openProps = canOpen
      ? {
          className: 'cursor-pointer transition-all hover:bg-accent/50 hover:shadow-sm',
          onClick: () => handleOpenForItem(item.id),
          onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
            // Only the card itself opens the drawer on Enter/Space — ignore keydown
            // bubbling up from nested interactive children.
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenForItem(item.id); }
          },
          role: 'button',
          tabIndex: 0,
          'aria-label': householdName,
        }
      : {};
    return (
      <Card {...openProps} data-testid={`board-card-${item.id}`}>
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold truncate">{householdName}</span>
            <Badge variant="secondary" className="text-xs px-1.5 py-0">{stageLabel}</Badge>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="tabular-nums">{formatMoneyCents(outstandingCents, currency)}</span>
            {isPaused && (
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                {i18n._({ id: 'dunning.kanban.paused', message: 'Paused' })}
              </Badge>
            )}
          </div>
          {daysOverdue !== null && daysOverdue > 0 && (
            <Badge variant="destructive" className="text-xs px-1.5 py-0" data-testid={`days-overdue-${item.id}`}>
              {i18n._(
                msg({ id: 'dunning.kanban.daysOverdue', message: '{count, plural, one {# day overdue} other {# days overdue}}' }),
                { count: daysOverdue },
              )}
            </Badge>
          )}
          {lastSent && (
            <div className="text-xs text-muted-foreground">
              {i18n._({ id: 'dunning.kanban.lastSent', message: 'Last sent' })}: {formatDateDisplay(lastSent, i18n.locale)}
            </div>
          )}
          {notTakenTag}
          {!canOpen && <ModuleUnavailableHint />}
        </CardContent>
      </Card>
    );
  }

  // change_request renders a triage-specific layout: sub_type + sensitivity
  // badge, a confidence chip, a source badge, and the first available action.
  if (item.category === 'change_request') {
    const view = parseChangeRequestPayload(item.payload);
    const subType = view?.sub_type ?? '';
    const sensitive = isSensitiveSubType(subType);
    const confidenceValue = view?.resolved?.confidence;
    const hasConfidence = typeof confidenceValue === 'number' && confidenceValue > 0;
    const sourceKind = view?.source?.kind;
    const commentValue =
      (view?.executor?.body?.comment as { value?: string } | undefined)?.value;
    const typeLabel = getChangeRequestTypeLabel(i18n, view);
    const cardSummary = commentValue || typeLabel;
    // Subject line (A1): who the request is about and who sent it, plus when.
    const subjectText = buildChangeRequestSubjectText(i18n, view, item.change_request_subject);
    const submittedAt = item.created_at ? parseISO(item.created_at) : null;
    const hasSubmittedAt = submittedAt !== null && isValid(submittedAt);
    return (
      <Card
        className="cursor-pointer transition-all hover:bg-accent/50 hover:shadow-sm"
        onClick={() => onOpenDetail(item.id)}
        onKeyDown={(e) => {
          // Only the card itself opens the detail on Enter/Space — ignore keydown
          // bubbling up from the nested action button so its activation doesn't
          // also open the sheet.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(item.id); }
        }}
        role="button"
        tabIndex={0}
        aria-label={subjectText ? `${typeLabel}: ${subjectText}` : typeLabel}
        data-testid={`board-card-${item.id}`}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className={cn("h-2 w-2 rounded-full flex-shrink-0", categoryColorClass)}
              aria-hidden="true"
            />
            <Badge variant="secondary" className="text-xs px-1.5 py-0" data-testid={`cr-subtype-${item.id}`}>
              {typeLabel}
            </Badge>
            {sensitive && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0" data-testid={`cr-sensitive-${item.id}`}>
                {i18n._({ id: 'changeRequest.badge.sensitive', message: 'Sensitive' })}
              </Badge>
            )}
            {lockReason && <BatchLockHint reason={lockReason} className="ml-auto" />}
          </div>
          {(subjectText || hasSubmittedAt) && (
            <div
              className="flex items-center gap-1 min-w-0 text-xs text-muted-foreground"
              data-testid={`cr-subject-${item.id}`}
            >
              {subjectText && (
                <span className="truncate" title={subjectText} data-testid={`cr-subject-names-${item.id}`}>
                  {subjectText}
                </span>
              )}
              {subjectText && hasSubmittedAt && <span aria-hidden="true">·</span>}
              {hasSubmittedAt && (
                <time
                  className="shrink-0 whitespace-nowrap"
                  dateTime={item.created_at}
                  title={i18n._({
                    id: 'changeRequest.card.submittedAt',
                    message: 'Submitted {date}',
                    values: { date: formatDateTime(submittedAt, i18n.locale) },
                  })}
                  data-testid={`cr-submitted-${item.id}`}
                >
                  {formatDistanceToNow(submittedAt, { addSuffix: true, locale: getDateFnsLocale(i18n.locale) })}
                </time>
              )}
            </div>
          )}
          <p className="text-sm font-medium leading-tight line-clamp-2">{cardSummary}</p>
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            {hasConfidence && (
              <Badge variant="outline" className="text-xs px-1.5 py-0" data-testid={`cr-confidence-${item.id}`}>
                {i18n._({ id: 'workflow.triage.confidence', message: 'Confidence' })}: {formatConfidence(confidenceValue)}
              </Badge>
            )}
            {sourceKind && (
              <Badge variant="outline" className="text-xs px-1.5 py-0" data-testid={`cr-source-${item.id}`}>
                {getSourceLabel(i18n, sourceKind)}
              </Badge>
            )}
          </div>
          {notTakenTag}
          {actions.length > 0 && (
            <Button
              size="sm"
              variant={actions[0] === 'reject' ? 'destructive' : 'default'}
              className="w-full h-7 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onActionClick(item.id, actions[0], item.current_state ?? '');
              }}
            >
              {getActionLabel(i18n, actions[0])}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="cursor-pointer transition-all hover:bg-accent/50 hover:shadow-sm"
      onClick={() => onOpenDetail(item.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(item.id); } }}
      role="button"
      tabIndex={0}
      aria-label={summary ?? getCategoryLabel(i18n, item.category ?? '')}
      data-testid={`board-card-${item.id}`}
    >
      <CardContent className="p-3 space-y-2">
        {/* Category + severity */}
        <div className="flex items-center gap-2">
          <div
            className={cn("h-2 w-2 rounded-full flex-shrink-0", categoryColorClass)}
            aria-hidden="true"
          />
          <span className="text-xs text-muted-foreground truncate">
            {getCategoryLabel(i18n, item.category ?? '')}
          </span>
          {item.category === 'accident_notification' && <AccidentSeverityBadge severity={severity} />}
        </div>

        {/* Summary */}
        <p className="text-sm font-medium leading-tight line-clamp-2">
          {summary ?? '—'}
        </p>

        {responsible && (
          <ResponsibleCell responsible={responsible} compact data-testid={`board-card-responsible-${item.id}`} />
        )}

        {notTakenTag}

        {/* Due + urgency */}
        {item.due_at && (
          <div className="flex items-center gap-2">
            {urgency === 'overdue' && (
              <StatusBadge tone="danger" label={i18n._({ id: 'workflow.urgency.overdue', message: 'Overdue' })} />
            )}
            {urgency === 'urgent' && (
              <StatusBadge tone="warning" label={i18n._({ id: 'workflow.urgency.urgent', message: 'Urgent' })} />
            )}
            <span className="text-xs text-muted-foreground">{formatDateTime(item.due_at, i18n.locale)}</span>
          </div>
        )}

        {/* Escalation target (issue #1297). The backend populates this for
            OVERDUE, non-completed items only, so a non-overdue card renders
            exactly as before. It stays in both board layouts: it says who
            takes over when nobody acts. */}
        {escalationLabel && (
          <div className="text-xs text-destructive" data-testid={`escalates-to-${item.id}`}>
            {i18n._({ id: 'workflow.detail.escalatesTo', message: 'Escalates to' })}:{' '}
            <span className="font-medium">{escalationLabel}</span>
          </div>
        )}

        {/* First available action */}
        {actions.length > 0 && (
          <Button
            size="sm"
            variant={actions[0] === 'escalate' ? 'destructive' : 'default'}
            className="w-full h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onActionClick(item.id, actions[0], item.current_state ?? '');
            }}
          >
            {getActionLabel(i18n, actions[0])}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
